import React, {FunctionComponent, useRef, useState} from 'react'
import { TextInput } from 'react-native'
import { useTranslation } from 'react-i18next'
import {IProps} from "./IProps";
import {
  FieldInputContainer, InputError,
  FieldLabel, FieldInput
} from "../../../App/Components/InputField/style";
import {hexToRgba} from "../../../App/Util";
import {checkGiftCard} from "./Client/checkGiftCard";
import {mapGiftCardLookupResponse, GiftCardLookupOutcome, GiftCardLookupResult} from "./mapGiftCardLookupResponse";
import {createLatestOnly} from "./createLatestOnly";

type GiftCardLookupRequest = { code: string; establishmentId: string }

const lookupGiftCard = ({ code, establishmentId }: GiftCardLookupRequest): Promise<GiftCardLookupResult> =>
  checkGiftCard(code, establishmentId)
    .then((response) => ({ status: response.status, data: response.data }))
    .catch((error) => ({ status: error.response?.status, data: error.response?.data }))

export const GiftCardField: FunctionComponent<IProps> = ({
  isShowGiftCardField,
  setGiftCarInput,
  giftCardInput,
  setGiftCardState,
  establishmentId,
  fontColor
}) => {
  const [errorMessage, setErrorMessage] = useState<GiftCardLookupOutcome | 'idle' | 'checkingCard'>('idle')
  const messageContent = {
    warning: 'This card has no value left' ,
    error: 'This code is not valid',
    expired: 'This gift card has expired',
    success: 'Your gift card will be deducted from the deposit',
    checkingCard: 'Checking your card please wait',
  }
  const messageColor = {
    warning: '#d27a46' ,
    error: '#d24444',
    expired: '#d24444',
    success: '#46d259',
    checkingCard: '#4673d2',
  }

  const { t } = useTranslation()
  const ref = useRef<TextInput>()

  const onPressFieldLabel = () => ref.current.focus()
  const skipLabelFocus = () => ref.current.focus()

  const latestLookup = useRef(createLatestOnly(lookupGiftCard)).current

  const submitGiftCardCheck = async () => {
    if (giftCardInput.trim() === '') {
      latestLookup.cancel()
      setErrorMessage('idle')
      return setGiftCardState('idle')
    }
    setGiftCardState('checkingCard')
    setErrorMessage('checkingCard')
    const lookupResult = await latestLookup.run({ code: giftCardInput, establishmentId })
    if (lookupResult === undefined) {
      return undefined
    }
    const outcome = mapGiftCardLookupResponse(lookupResult)
    setErrorMessage(outcome)
    if (outcome === 'error') {
      return setGiftCardState('invalid')
    }
    if (outcome === 'expired') {
      return setGiftCardState('expired')
    }
    return setGiftCardState('idle')
  }

  const fadedFontColor: string = hexToRgba(fontColor, 0.6)

  if(!isShowGiftCardField){
    return null
  }
  return (
        <FieldInputContainer>
            <FieldLabel
                fontcolor={fadedFontColor}
                onPress={onPressFieldLabel}
                onFocus={skipLabelFocus}
            >
                {t('Gift card number')}
            </FieldLabel>
            <FieldInput
                fontcolor={fontColor}
                onBlur={submitGiftCardCheck}
                value={giftCardInput}
                onChange={(e)=>setGiftCarInput(e.target.value)}
                ref={ref}
            />
                <InputError $showError={errorMessage !== 'idle'} $statusColor={messageColor[errorMessage]}>
                    {t(messageContent[errorMessage])}
                </InputError>
        </FieldInputContainer>
  )
}